import { useRef, useState } from "react";
import { ImagePlus, Loader2, X, ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { errMsg, imgUrl } from "@/lib/api";
import { uploadImage } from "@/components/ImageUpload";
import { BannerSchedule, BannerStatusBadge, toBanner } from "@/components/menu/BannerSchedule";
import { toast } from "sonner";

const MAX_BANNERS = 5;

function UploadButton({ onUploaded, label, testId, disabled }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("A imagem deve ter no máximo 5 MB");
    setBusy(true);
    try { onUploaded(await uploadImage(file)); toast.success("Imagem enviada. Clique em Salvar para publicar."); }
    catch (err) { toast.error(errMsg(err, "Não foi possível enviar a imagem")); }
    finally { setBusy(false); }
  };
  return (
    <>
      <Button type="button" size="sm" variant="outline" disabled={busy || disabled} onClick={() => ref.current?.click()} data-testid={testId}>
        {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <ImagePlus className="h-4 w-4 mr-1" />}{label}
      </Button>
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={pick} data-testid={`${testId}-input`} />
    </>
  );
}

export function LogoField({ value, onChange }) {
  return (
    <div className="space-y-2" data-testid="logo-field">
      <p className="text-sm font-medium">Logo da loja</p>
      <div className="flex items-center gap-3">
        <div className="h-24 w-24 shrink-0 overflow-hidden rounded-xl border bg-muted grid place-items-center">
          {value ? <img src={imgUrl(value)} alt="Logo" className="h-full w-full object-cover" data-testid="logo-preview" /> : <ImagePlus className="h-6 w-6 text-muted-foreground/60" />}
        </div>
        <div className="flex flex-col gap-2">
          <UploadButton onUploaded={onChange} label={value ? "Trocar logo" : "Enviar logo"} testId="logo-upload-btn" />
          {value && <Button type="button" size="sm" variant="ghost" onClick={() => onChange("")} data-testid="logo-remove-btn"><X className="h-4 w-4 mr-1" />Remover</Button>}
        </div>
      </div>
      <p className="text-xs text-muted-foreground">Imagem quadrada, ideal 500 x 500 px. JPG, PNG ou WEBP até 5 MB.</p>
    </div>
  );
}

function BannerItem({ b, i, last, onMove, onRemove, onEdit }) {
  return (
    <div className="overflow-hidden rounded-lg border" data-testid={`banner-item-${i}`}>
      <img src={imgUrl(b.url)} alt={`Banner ${i + 1}`} className="aspect-[16/5] w-full object-cover" />
      <div className="flex items-center gap-1 p-2 text-xs">
        <span className="font-medium">Banner {i + 1}</span>
        <span className="flex-1"><BannerStatusBadge b={b} testId={`banner-status-${i}`} /></span>
        <Button size="icon" variant="ghost" className="h-7 w-7" disabled={i === 0} onClick={() => onMove(i, -1)} aria-label="Mover para antes" data-testid={`banner-left-${i}`}><ArrowLeft className="h-4 w-4" /></Button>
        <Button size="icon" variant="ghost" className="h-7 w-7" disabled={last} onClick={() => onMove(i, 1)} aria-label="Mover para depois" data-testid={`banner-right-${i}`}><ArrowRight className="h-4 w-4" /></Button>
        <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => onRemove(i)} aria-label="Remover banner" data-testid={`banner-remove-${i}`}><X className="h-4 w-4" /></Button>
      </div>
      <BannerSchedule b={b} i={i} onChange={onEdit} />
    </div>
  );
}

export function BannersField({ value, onChange }) {
  const move = (i, d) => { const l = [...value]; [l[i], l[i + d]] = [l[i + d], l[i]]; onChange(l); };
  return (
    <div className="space-y-3" data-testid="banners-field">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-sm font-medium">Banners da página da loja ({value.length}/{MAX_BANNERS})</p>
          <p className="text-xs text-muted-foreground">Ideal 1600 x 500 px. Com 1 banner ele fica fixo; com 2 ou mais eles passam sozinhos a cada 5 segundos. Cada banner pode ter um período e dias da semana: fora deles ele some da loja sozinho.</p>
        </div>
        <UploadButton onUploaded={(url) => onChange([...value, toBanner(url)])} label="Adicionar banner" testId="banner-upload-btn" disabled={value.length >= MAX_BANNERS} />
      </div>
      {value.length === 0 ? (
        <p className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground" data-testid="banners-empty">Nenhum banner enviado. A loja mostra a cor da marca no lugar.</p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {value.map((b, i) => <BannerItem key={b.url} b={b} i={i} last={i === value.length - 1} onMove={move} onRemove={(j) => onChange(value.filter((_, k) => k !== j))} onEdit={(nb) => onChange(value.map((x, k) => (k === i ? nb : x)))} />)}
        </div>
      )}
    </div>
  );
}
